import { Table as TableComponent } from '@/components/ui/Table';
import { flexRender, Table } from '@tanstack/react-table';

import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { ClientDevice, MediaKind } from '@/models/device.models';
import { SiteConfig } from '@/models/site.models';
import { UTC_ZONE } from '@/models/time.models';
import { RoiConfig } from '@/models/zone.models';
import { getDeviceMedia, getMediaEndpointUrl } from '@/utils/media.functions';
import {
  getGridColsClass,
  humanizeDurationWithPrefix,
} from '@/utils/ui.function';
import {
  ArrowDownWideNarrow,
  ArrowUpDown,
  ArrowUpNarrowWide,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from './ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/Select';
import { Spinner } from './ui/Spinner';
import { ItemsPerRowSelector } from './ui/custom-ui/ItemsPerRowSelector';
import { PolygonData } from './ui/polygon/PolygonOverlay';
import { PolygonOverlayWrapper } from './ui/polygon/PolygonOverlayWrapper';

export type ViewMode = 'grid' | 'list';
interface DataTableProps<T> {
  table: Table<T>;
  selectRowEnabled?: boolean;
  loading?: boolean;
  fetching?: boolean;
  viewMode?: ViewMode;
}
export function DataTable<T>({
  loading,
  fetching,
  selectRowEnabled,
  table,
  viewMode = 'list',
}: DataTableProps<T>) {
  const [numberOfColumns, setNumberOfColumns] = useState(4);
  return (
    <>
      <div className="flex flex-col gap-4">
        {viewMode === 'grid' && (
          <div className="flex justify-between gap-4">
            <div>
              {table?.getHeaderGroups().map((headerGroup) => (
                <div key={headerGroup.id} className="flex flex-wrap gap-1">
                  {headerGroup.headers
                    .filter((h) => h.column.getCanSort())
                    .map((header, index) => {
                      return (
                        <Button
                          key={index}
                          size={'sm'}
                          variant={
                            header.column.getIsSorted() ? 'default' : 'ghost'
                          }
                          onClick={() => header.column.toggleSorting()}
                        >
                          <span className="capitalize">{header.id}</span>
                          {header.column.getIsSorted() === 'asc' ? (
                            <ArrowUpNarrowWide className="h-4 w-4" />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ArrowDownWideNarrow className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4" />
                          )}
                        </Button>
                      );
                    })}
                </div>
              ))}
            </div>
            <ItemsPerRowSelector
              value={numberOfColumns}
              onChange={setNumberOfColumns}
            />
          </div>
        )}
        <div
          className={`relative rounded-md ${viewMode === 'list' && 'border'}`}
        >
          {(fetching || loading) && (
            <>
              <div className="bg-secondary absolute z-10 h-full w-full opacity-25"></div>
              <div className="absolute top-1/2 left-1/2 z-20 mt-7 h-16 -translate-x-1/2 -translate-y-1/2">
                <Spinner className="text-muted-foreground h-10 opacity-100" />
              </div>
            </>
          )}

          {viewMode === 'grid' ? (
            <div className={`grid ${getGridColsClass(numberOfColumns)} gap-4`}>
              {table.getRowModel().rows.map((row) => {
                const device = row.original as ClientDevice;
                const polygonData: PolygonData[] = (device.rois ?? []).map(
                  (roi) => {
                    return {
                      polygon: (roi.config as RoiConfig).Points,
                      name: roi.name,
                    };
                  },
                );
                const media = getDeviceMedia(device);
                const mediaEndpointUrl = getMediaEndpointUrl({
                  mediaId: media?.mediaId ?? '',
                  timestamp: media?.time,
                });
                const humanizeDuration =
                  media?.kind !== MediaKind.ORIGINAL &&
                  media?.time &&
                  device.lastMedia?.media
                    ? humanizeDurationWithPrefix({
                        dateTime: DateTime.fromISO(media.time),
                        dateTimeToCompare: DateTime.fromISO(
                          device.lastMedia?.media.time,
                        ),
                        units: ['h', 'm', 's', 'ms'],
                      })
                    : null;

                return (
                  <div
                    key={device.id}
                    className="flex min-h-24 flex-col rounded-t-md border p-0"
                  >
                    <div className="bg-secondary flex flex-col rounded-t-md px-2 pt-3 pb-1">
                      <span className="truncate leading-none font-semibold">
                        <Link to={`${device.id}`}>{device.name}</Link>
                      </span>

                      <div className="text-muted-foreground truncate text-sm leading-5">
                        {device.lastMedia?.lastOriginalMediaTime
                          ? DateTime.fromISO(
                              device.lastMedia?.lastOriginalMediaTime,
                              { zone: UTC_ZONE },
                            )
                              .setZone(
                                (device.site?.config as SiteConfig)?.Timezone,
                              )
                              .toLocaleString(
                                DateTime.DATETIME_MED_WITH_SECONDS,
                              )
                          : '\u00A0'}
                        {humanizeDuration && `(${humanizeDuration})`}
                      </div>
                    </div>

                    <div className="flex flex-1 items-center justify-center">
                      <PolygonOverlayWrapper
                        mediaEndpointUrl={mediaEndpointUrl}
                        polygonData={polygonData}
                        isVideo={media?.mimeRootType === 'video'}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <TableComponent>
              <TableHeader className="bg-muted sticky top-0">
                {table?.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          style={{
                            minWidth: header.column.columnDef.size,
                            maxWidth: header.column.columnDef.size,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={table.getAllColumns().length}>
                      <span className="text-muted-foreground text-md flex h-14 items-center justify-center">
                        No data
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </TableComponent>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-muted-foreground flex-1 text-sm">
            {selectRowEnabled ? (
              <>
                {table.getFilteredSelectedRowModel().rows.length} of{' '}
                {table.getFilteredRowModel().rows.length} row(s) selected.
              </>
            ) : (
              <>{table.getRowCount()} row(s) in total.</>
            )}
          </div>
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">Rows per page</p>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(Number(value));
                }}
              >
                <SelectTrigger className="w-[80px]" size={'sm'}>
                  <SelectValue
                    placeholder={table.getState().pagination.pageSize}
                  />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 50, 100, 150, 200, 300].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <div className="flex items-center justify-center text-sm font-medium">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size={'icon-sm'}
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  <span className="sr-only">Go to first page</span>
                  <ChevronsLeft />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => table.previousPage()}
                  size={'icon-sm'}
                  disabled={!table.getCanPreviousPage()}
                >
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeft />
                </Button>
                <Button
                  variant="outline"
                  size={'icon-sm'}
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <span className="sr-only">Go to next page</span>
                  <ChevronRight />
                </Button>
                <Button
                  variant="outline"
                  size={'icon-sm'}
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  <span className="sr-only">Go to last page</span>
                  <ChevronsRight />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
